import { Epic, Story, Sprint } from '../types';
import { connectContext, ConnectContext } from './connectContext';

// Connect-specific Jira API service that uses the Connect context
export class ConnectJiraApiService {
  private context: ConnectContext | null = null;
  private baseUrl: string | null = null;

  constructor() {
    this.initialize();
  }

  private async initialize() {
    // Wait for connect context to be available
    const checkContext = () => {
      this.context = connectContext.getContext();
      this.baseUrl = connectContext.getJiraBaseUrl();
      
      if (!this.context || !this.baseUrl) {
        setTimeout(checkContext, 100);
      }
    };
    
    checkContext();
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (!this.baseUrl) {
      throw new Error('Jira base URL not available');
    }

    const url = `/api/jira/${endpoint.replace(/^\//, '')}`;
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`Jira API Error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Connect Jira API Request Failed:', error);
      throw error;
    }
  }

  private async makeAgileRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const agileEndpoint = `agile/1.0/${endpoint.replace(/^\//, '')}`;
    return this.makeRequest<T>(agileEndpoint, options);
  }

  // Get current project key from context
  private getCurrentProjectKey(): string {
    const projectKey = connectContext.getCurrentProjectKey();
    if (!projectKey) {
      throw new Error('No project context available');
    }
    return projectKey;
  }

  // Get all boards for the current project
  async getBoards(): Promise<any[]> {
    const response = await this.makeAgileRequest<{ values: any[] }>('board');
    const projectKey = this.getCurrentProjectKey();
    
    return response.values.filter(board => 
      board.name.includes(projectKey) || board.type === 'scrum'
    );
  }

  // Get sprints for a board
  async getSprints(boardId: number): Promise<Sprint[]> {
    const response = await this.makeAgileRequest<{ values: any[] }>(
      `board/${boardId}/sprint?state=active,closed,future`
    );

    return response.values.map(sprint => ({
      id: sprint.id.toString(),
      name: sprint.name,
      goal: sprint.goal || '',
      startDate: sprint.startDate ? new Date(sprint.startDate) : new Date(),
      endDate: sprint.endDate ? new Date(sprint.endDate) : new Date(),
      status: sprint.state === 'closed' ? 'completed' : 
              sprint.state === 'active' ? 'active' : 'future',
      stories: [],
      velocity: 0,
      totalPoints: 0,
      completedPoints: 0
    }));
  }

  // Get issues for a sprint
  async getSprintIssues(boardId: number, sprintId: string): Promise<Story[]> {
    const response = await this.makeAgileRequest<{ issues: any[] }>(
      `board/${boardId}/sprint/${sprintId}/issue`
    );

    return response.issues
      .filter(issue => issue.fields.parent)
      .map(this.mapJiraIssueToStory);
  }

  // Get all epics for the current project
  async getEpics(): Promise<Epic[]> {
    const projectKey = this.getCurrentProjectKey();
    const jql = `project = "${projectKey}" AND issuetype = Epic ORDER BY created DESC`;
    const response = await this.makeRequest<{ issues: any[] }>(
      `search?jql=${encodeURIComponent(jql)}&maxResults=100`
    );

    return response.issues.map(this.mapJiraIssueToEpic);
  }

  // Get stories for an epic
  async getEpicStories(epicKey: string): Promise<Story[]> {
    const jql = `"Epic Link" = "${epicKey}" ORDER BY rank ASC`;
    const response = await this.makeRequest<{ issues: any[] }>(
      `search?jql=${encodeURIComponent(jql)}&maxResults=100`
    );

    return response.issues.map(this.mapJiraIssueToStory);
  }

  // Get worklog for an issue
  async getIssueWorklogs(issueKey: string) {
    const response = await this.makeRequest<{ worklogs: any[] }>(
      `issue/${issueKey}/worklog`
    );

    return response.worklogs.map(worklog => ({
      id: worklog.id,
      author: worklog.author.displayName,
      timeSpent: this.parseJiraTime(worklog.timeSpent),
      started: new Date(worklog.started),
      comment: worklog.comment || ''
    }));
  }

  // Log work on an issue
  async logWork(issueKey: string, timeSpent: string, comment?: string) {
    const worklog = {
      timeSpent,
      comment: comment || '',
      started: new Date().toISOString()
    };

    return await this.makeRequest(`issue/${issueKey}/worklog`, {
      method: 'POST',
      body: JSON.stringify(worklog)
    });
  }

  // Update issue
  async updateIssue(issueKey: string, fields: any) {
    return await this.makeRequest(`issue/${issueKey}`, {
      method: 'PUT',
      body: JSON.stringify({ fields })
    });
  }

  // Transition issue status
  async transitionIssue(issueKey: string, transitionId: string) {
    return await this.makeRequest(`issue/${issueKey}/transitions`, {
      method: 'POST',
      body: JSON.stringify({
        transition: { id: transitionId }
      })
    });
  }

  // Helper methods
  private mapJiraIssueToEpic = (issue: any): Epic => ({
    id: issue.id,
    key: issue.key,
    title: issue.fields.summary,
    description: issue.fields.description || '',
    status: this.mapJiraStatus(issue.fields.status.statusCategory.key),
    priority: this.mapJiraPriority(issue.fields.priority.name),
    assignee: issue.fields.assignee?.displayName || 'Unassigned',
    storyPoints: issue.fields.customfield_10016 || 0,
    stories: [],
    startDate: new Date(issue.fields.created),
    endDate: new Date(),
    color: issue.fields.epic?.color || this.getRandomColor()
  });

  private mapJiraIssueToStory = (issue: any): Story => ({
    id: issue.id,
    key: issue.key,
    title: issue.fields.summary,
    description: issue.fields.description || '',
    status: this.mapJiraStatus(issue.fields.status.statusCategory.key),
    priority: this.mapJiraPriority(issue.fields.priority.name),
    assignee: issue.fields.assignee?.displayName || 'Unassigned',
    storyPoints: issue.fields.customfield_10016 || 0,
    timeSpent: this.parseJiraTime(issue.fields.timetracking?.timeSpent),
    remainingEstimate: this.parseJiraTime(issue.fields.timetracking?.remainingEstimate),
    subtasks: [],
    epicId: issue.fields.parent?.key || ''
  });

  private mapJiraStatus(statusCategory: string): 'todo' | 'in-progress' | 'done' {
    switch (statusCategory.toLowerCase()) {
      case 'done':
        return 'done';
      case 'indeterminate':
        return 'in-progress';
      default:
        return 'todo';
    }
  }

  private mapJiraPriority(priority: string): 'high' | 'medium' | 'low' {
    switch (priority.toLowerCase()) {
      case 'highest':
      case 'high':
        return 'high';
      case 'medium':
        return 'medium';
      default:
        return 'low';
    }
  }

  private parseJiraTime(timeString?: string): number {
    if (!timeString) return 0;
    
    const hours = timeString.match(/(\d+)h/);
    const minutes = timeString.match(/(\d+)m/);
    const days = timeString.match(/(\d+)d/);
    
    let totalHours = 0;
    if (days) totalHours += parseInt(days[1]) * 8;
    if (hours) totalHours += parseInt(hours[1]);
    if (minutes) totalHours += parseInt(minutes[1]) / 60;
    
    return Math.round(totalHours * 100) / 100;
  }

  private getRandomColor(): string {
    const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4'];
    return colors[Math.floor(Math.random() * colors.length)];
  }
}

export const connectJiraApi = new ConnectJiraApiService();